<template>
  <div class="search">
    <div class="search-field-selector">
      <div class="search-field-selector__dropdown">
        <h6
          v-click-outside="outsideClicked"
          @click="isActiveDropdown = !isActiveDropdown"
        >
          {{ dropdownTitle }}
          <span
            v-if="!isSingleOptions"
            :class="{ active: isActiveDropdown }"
            class="caret"
          />
        </h6>
        <ul v-if="isActiveDropdown && !isSingleOptions">
          <li
            v-for="(option, index) in options"
            :key="index"
            class="option"
            @click="selectValue(option)"
          >
            {{ option.label }}
            <svg-loader
              v-if="selectedValue?.value === option.value"
              src="/static/images/check-dropdown.svg"
            />
          </li>
        </ul>
      </div>
      <div class="search-field-selector__field">
        <input
          v-if="shouldDisplayTextInput"
          v-model="query"
          maxlength="199"
          :placeholder="placeholderType"
          data-vv-validate-on="change|blur"
          :name="type"
          @input="notifyParent"
          @keydown.enter="onEnterPressed"
        />

        <DatePicker
          v-if="type === 'singleDate'"
          :date-selected.sync="singleDate"
          :date-filter-alternative="true"
          :limit-data="limitDateComputed"
          :calendar-direction="'bottom'"
          date-type="single"
          @update:dateSelected="onDateChange"
        />

        <DatePickerRange
          v-else-if="type === 'date' || type === 'dateRange'"
          :date-selected.sync="date"
          :date-filter-alternative="true"
          :limit-data="limitDateComputed"
          :calendar-direction="'bottom'"
          :clear-button="clearButton"
          :default-start-date="defaultStartDate"
          :default-end-date="defaultEndDate"
          @update:dateSelected="onDateChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DatePickerRange from '@/components/common/DatePickerRange';
import DatePicker from '@/components/common/DatePicker';
import Moment from 'moment';
import { clickOutside } from '@/utils/directives';

export default {
  name: 'SearchFieldSelector',
  components: {
    DatePickerRange,
    DatePicker,
  },
  directives: {
    'click-outside': {
      bind: function (el, binding, vnode) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unbind: function (el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      },
    },
    clickOutside,
  },
  props: {
    /**
     * Component properties, each documented with JSDoc.
     * @property {Array<Object>} options - Array of option objects for the dropdown.
     * Each object in the array should have:
     *  @property {String} value - The value of the option, used as a key identifier.
     *  @property {String} label - The display label for the option in the dropdown.
     *  @property {String} type - The type of input this option represents (e.g., 'date', 'singleDate', 'text').
     *  @property {String} [placeholder] - Optional placeholder text for the input field when this option is selected.
     *  @property {Number} [limitDays] - Optional number of days to limit date selection in date pickers.
     */
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    defaultStartDate: {
      type: [Date, String],
      default: () => new Date(),
    },
    defaultEndDate: {
      type: [Date, String],
      default: () => new Date(),
    },
    cleanDate: {
      type: Number,
      default: 0,
    },
    limitDate: {
      type: Date,
      default: null,
    },
    clearButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedValue: this.options[0] ?? null,
      query: '',
      date: {
        start: new Date(),
        end: new Date(),
      },
      singleDate: new Date(),
      isActiveDropdown: false,
    };
  },
  computed: {
    type() {
      return this.selectedValue ? this.selectedValue?.type : '';
    },
    placeholderType() {
      return this.selectedValue?.placeholder ?? '';
    },
    dropdownTitle() {
      return this.selectedValue?.label ?? '';
    },
    today() {
      return Moment(new Date()).format('DD/MM/YYYY');
    },
    limitDateComputed() {
      if (this.limitDate) return this.limitDate;

      const { limitDays } = this.selectedValue;
      return limitDays ? Moment(new Date()).subtract(limitDays, 'days').toDate() : Moment(new Date()).toDate();
    },
    shouldDisplayTextInput() {
      return this.type !== 'date' && this.type !== 'singleDate';
    },
    isSingleOptions() {
      return this.options.length === 1;
    },
  },
  watch: {
    cleanDate: {
      handler: function (warning) {
        if (warning) {
          this.date.start = new Date();
          this.date.end = new Date();
        }
      },
      deep: true,
    },
  },
  methods: {
    selectValue(selected) {
      this.isActiveDropdown = false;
      this.selectedValue = selected;

      if (this.type === 'date' || this.type === 'dateRange') {
        const dataAtual = new Date();

        this.date = {
          start: this.defaultStartDate ?? dataAtual,
          end: this.defaultEndDate ?? dataAtual,
        };
      }

      this.notifyParent();
    },
    outsideClicked() {
      this.isActiveDropdown = false;
    },
    onDateChange(newValue) {
      if (this.type === 'date' || this.type === 'dateRange') {
        if (typeof newValue === 'object' && newValue.start && newValue.end) {
          this.date = newValue;
        } else {
          this.singleDate = newValue;
        }
      }
      this.notifyParent();
    },
    notifyParent() {
      const searchData = {
        ...this.selectedValue,
        type: this.type,
        query: this.query,
        date: this.date,
        singleDate: this.singleDate,
      };
      this.$emit('updateSearchValue', searchData);
    },
    onEnterPressed() {
      this.$emit('enterPressed');
    },
  },
};
</script>

<style lang="scss">
.search {
  margin-top: 0.5em;
  width: 100%;

  .search-field-selector {
    display: flex;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 100%;
    flex-wrap: nowrap;
    justify-content: space-between;
    min-height: 40px;
    align-items: center;

    h6 {
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-width: 150px;
      padding: 8px;
      font-weight: 500;
      font-size: 13px;
      color: #999;
      cursor: pointer;

      .caret {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid #7f1a54;
        margin-left: 4px;
        border-radius: 6px;
        transform: rotate(180deg);
        transition: all ease-out 0.3s;

        &.active {
          transform: rotate(0deg);
        }
      }
    }

    &__dropdown {
      border-right: 1px solid #ccc;
      position: relative;

      ul {
        width: 142px;
        display: flex;
        flex-wrap: wrap;
        padding: 8px;
        background: #ffffff;
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
        border-radius: 4px;
        position: absolute;
        left: 0;
        top: 44px;
        z-index: 1;
      }

      li {
        width: 100%;
        padding: 9px;
        margin: 0;
        font-size: 14px;
        color: #363636;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #ffffff;
        transition: all ease 0.3s;
        cursor: pointer;

        &:hover {
          background-color: #e9e9e9;
          border-radius: 4px;
        }
      }
    }

    &__field {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-grow: 1;
      flex-shrink: 1;
      padding: 0 8px;

      input {
        font-weight: 500;
        font-size: 13px;
        line-height: 16px;
        color: #999999;
        height: 50px;
        border: none;
        flex-grow: 1;
        outline: none;
        background-color: transparent;
      }

      #datePickerSelect {
        margin: 0;
        padding: 0;
        align-items: center;
      }

      #datePickerSingleSelect {
        min-height: inherit;
        margin: 0;
        padding: 0;
        align-items: center;
      }
    }
  }
}
</style>
